/* import __COLOCATED_TEMPLATE__ from './scim.hbs'; */
/* RESPONSIBLE TEAM: team-app-security */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';

import type IntlService from 'ember-intl/services/intl';
import type ScimManagementService from 'embercom/services/scim-management-service';

import type { ScimGroup, ScimArgs } from './scim/types';
import { ScimGroupState } from './scim/types';
import {
  SCIM_TABLE_COLUMNS,
  CHANGES_REVIEW_TABLE_COLUMNS,
  getUserInitials,
  createRoleDropdownList,
  getGroupStateDisplayName,
  getGroupStateColor,
  createRoleSelectGroupList,
} from './scim/utils';
import { getRoleNameById } from 'embercom/utils/teammates/role-dropdown-utils';

export default class Scim extends Component<ScimArgs> {
  @service declare intl: IntlService;
  @service declare scimManagementService: ScimManagementService;

  constructor(owner: unknown, args: ScimArgs) {
    super(owner, args);
    this.loadData();
  }

  get availableRoles() {
    return this.args.roles || [];
  }

  get scimGroupsTableColumns() {
    return SCIM_TABLE_COLUMNS(this.intl);
  }

  get changesReviewTableColumns() {
    return CHANGES_REVIEW_TABLE_COLUMNS(this.intl);
  }

  get canEditScim(): boolean {
    return this.scimManagementService.canEditScim;
  }

  get isLoadingEditPermission(): boolean {
    return this.scimManagementService.isLoadingEditPermission;
  }

  get hasUnsavedChanges(): boolean {
    return this.scimManagementService.changesState.hasChanges();
  }

  get scimGroups(): ScimGroup[] {
    return this.scimManagementService.currentState.groups;
  }

  get sortedScimGroups(): ScimGroup[] {
    return this.scimManagementService.changesState.sortedGroups;
  }

  get hasGroups(): boolean {
    return this.scimGroups.length > 0;
  }

  get hasCurrentGroupChanges(): boolean {
    return this.scimManagementService.selectedGroup
      ? Boolean(
          this.scimManagementService.changesState.roleChanges[
            this.scimManagementService.selectedGroup.id
          ],
        )
      : false;
  }

  get modalRoleDropdownList() {
    if (!this.scimManagementService.selectedGroup) {
      return [];
    }
    return createRoleDropdownList(this.availableRoles, this.intl, this.updateRoleInModal);
  }

  get modalRoleSelectGroupList() {
    if (!this.scimManagementService.selectedGroup) {
      return [];
    }
    let unassignableRoles = this.scimManagementService.getUnassignableRolesInfo();
    return createRoleSelectGroupList(this.availableRoles, this.intl, unassignableRoles);
  }

  get tableRoleSelectGroupList() {
    let unassignableRoles = this.scimManagementService.getUnassignableRolesInfo();
    return createRoleSelectGroupList(this.availableRoles, this.intl, unassignableRoles);
  }

  get currentModalRoleName(): string {
    if (!this.scimManagementService.selectedGroup) {
      return '';
    }

    let group = this.scimManagementService.selectedGroup;
    let roleChange = this.scimManagementService.changesState.roleChanges[group.id];
    let effectiveRoleId = roleChange !== undefined ? roleChange : group.role_id;

    return this.getRoleNameById(effectiveRoleId);
  }

  get pendingChangesForReview() {
    return this.scimManagementService.getPendingChangesForReview();
  }

  get hasIncludedChanges(): boolean {
    return this.scimManagementService.hasIncludedChanges();
  }

  @action
  canModifyGroupRole(groupId: string): boolean {
    return this.scimManagementService.canModifyGroupRole(groupId);
  }

  @action
  getDisabledGroupRoleTooltip(groupId: string): string | null {
    if (this.canModifyGroupRole(groupId)) {
      return null;
    }
    return this.intl.t('new-settings.workspace.teammates.scim-tab.tooltips.cannot-modify-role');
  }

  get groupChanges() {
    return this.scimManagementService.changesState.roleChanges;
  }

  get showDetailsModal(): boolean {
    return this.scimManagementService.showDetailsModal;
  }

  get showApplyChangesModal(): boolean {
    return this.scimManagementService.showApplyChangesModal;
  }

  get showGroupDeactivationConfirmationModal(): boolean {
    return this.scimManagementService.showGroupDeactivationConfirmationModal;
  }

  get selectedGroupForDetails(): ScimGroup | null {
    return this.scimManagementService.selectedGroup;
  }

  get canDeactivateSelectedGroup(): boolean {
    let selectedGroup = this.scimManagementService.selectedGroup;
    return Boolean(
      selectedGroup && selectedGroup.state === ScimGroupState.ACTIVE && this.canEditScim,
    );
  }

  get currentRoleNameForDeactivation(): string {
    if (!this.scimManagementService.selectedGroup) {
      return '';
    }
    let group = this.scimManagementService.selectedGroup;
    return this.getRoleNameById(group.role_id);
  }

  get isLoadingGroupDetails(): boolean {
    return this.scimManagementService.isLoadingGroupDetails;
  }

  get isLoading(): boolean {
    return this.scimManagementService.isLoading;
  }

  get isLoadingUserGroups(): boolean {
    return this.scimManagementService.isLoadingUserGroups;
  }

  @action
  getCurrentRoleForGroup(groupId: string | number): number | null {
    let group = this.scimGroups.find((g) => g.id === groupId.toString());
    let roleChange = this.scimManagementService.changesState.roleChanges[groupId.toString()];
    return roleChange !== undefined ? roleChange : (group?.role_id ?? null);
  }

  @action
  getCurrentRoleObjectForGroup(groupId: string | number) {
    let roleId = this.getCurrentRoleForGroup(groupId);
    if (!roleId) {
      return null;
    }
    return this.availableRoles.find((r) => r.id === roleId?.toString()) || null;
  }

  get currentModalRoleObject() {
    if (!this.scimManagementService.selectedGroup) {
      return null;
    }

    let group = this.scimManagementService.selectedGroup;
    let roleChange = this.scimManagementService.changesState.roleChanges[group.id];
    let effectiveRoleId = roleChange !== undefined ? roleChange : group.role_id;

    if (!effectiveRoleId) {
      return null;
    }

    return this.availableRoles.find((r) => r.id === effectiveRoleId?.toString()) || null;
  }

  @action
  getCurrentRoleNameForGroup(groupId: string | number): string {
    let roleId = this.getCurrentRoleForGroup(groupId);
    return this.getRoleNameById(roleId);
  }

  private getRoleNameById(roleId: number | null): string {
    return getRoleNameById(roleId, this.availableRoles, this.intl);
  }

  @action
  getRoleDropdownList(groupId: string | number) {
    return createRoleDropdownList(this.availableRoles, this.intl, (roleId) =>
      this.updateGroupRole(groupId, roleId),
    );
  }

  @action
  updateGroupRole(groupId: string | number, roleId: number | null) {
    this.scimManagementService.updateRole(groupId.toString(), roleId);
  }

  @action
  getUserInitials(user: Parameters<typeof getUserInitials>[0]) {
    return getUserInitials(user);
  }

  @action
  isUserExempt(userId: string): boolean {
    return this.scimManagementService.immuneAdminIds.includes(userId);
  }

  @action
  getGroupStateDisplayName(state: string | null) {
    return getGroupStateDisplayName(state, this.intl);
  }

  @action
  getGroupStateColor(state: string | null) {
    return getGroupStateColor(state);
  }

  @action
  getGroupPriority(group: ScimGroup): string {
    if (group.state === ScimGroupState.INACTIVE) {
      return '-';
    }
    return String(this.sortedScimGroups.findIndex((g) => g.id === group.id) + 1);
  }

  @action
  reorderGroups(reorderedGroups: ScimGroup[]) {
    if (!this.canEditScim) {
      return;
    }
    this.scimManagementService.reorderGroups(reorderedGroups);
  }

  @action
  async openGroupDetails(group: ScimGroup) {
    this.scimManagementService.openGroupDetails(group);

    try {
      await this.scimManagementService.loadGroupDetails(parseInt(group.id, 10));
    } catch (error) {
      console.error('Failed to load group details:', error);
    }
  }

  @action
  closeDetailsModal() {
    this.scimManagementService.closeGroupDetails();
  }

  @action
  updateRoleInModal(roleId: number | null) {
    if (!this.scimManagementService.selectedGroup) {
      return;
    }

    this.updateGroupRole(this.scimManagementService.selectedGroup.id, roleId);
  }

  @action
  onSelectRoleForGroup(groupId: string | number, role: any) {
    let roleId = role?.id ? parseInt(role.id, 10) : null;
    this.updateGroupRole(groupId, roleId);
  }

  @action
  onSelectModalRole(role: any) {
    let roleId = role?.id ? parseInt(role.id, 10) : null;
    this.updateRoleInModal(roleId);
  }

  @action
  async applyCurrentGroupChanges() {
    if (!this.scimManagementService.selectedGroup) {
      return;
    }

    let groupId = this.scimManagementService.selectedGroup.id;
    let hasChanges = Boolean(this.scimManagementService.changesState.roleChanges[groupId]);

    if (!hasChanges) {
      return;
    }

    try {
      await this.scimManagementService.applyChanges();
      this.scimManagementService.closeGroupDetails();
    } catch (error) {
      console.error('Failed to apply group changes:', error);
    }
  }

  @action
  openApplyChangesModal() {
    if (!this.canEditScim || !this.hasUnsavedChanges) {
      return;
    }
    this.scimManagementService.openApplyChangesModal();
  }

  @action
  closeApplyChangesModal() {
    this.scimManagementService.closeApplyChangesModal();
  }

  @action
  async confirmApplyChanges() {
    if (!this.canEditScim || !this.hasIncludedChanges) {
      this.closeApplyChangesModal();
      return;
    }

    await this.handleConfirmationApproved(() => this.closeApplyChangesModal());
  }

  @action
  toggleGroupChange(groupId: string): void {
    this.scimManagementService.toggleGroupChange(groupId);
  }

  @action
  toggleOrderChanges(): void {
    this.scimManagementService.toggleOrderChanges();
  }

  @action
  applyChanges() {
    this.openApplyChangesModal();
  }

  @action
  cancelChanges() {
    this.scimManagementService.resetChanges();
  }

  @action
  openGroupDeactivationConfirmation() {
    this.scimManagementService.openGroupDeactivationConfirmation();
  }

  @action
  closeGroupDeactivationConfirmation() {
    this.scimManagementService.closeGroupDeactivationConfirmation();
  }

  @action
  async confirmGroupDeactivation() {
    let selectedGroup = this.scimManagementService.selectedGroup;
    if (!selectedGroup) {
      return;
    }

    try {
      await this.scimManagementService.deactivateGroup(selectedGroup.id);
      this.scimManagementService.closeGroupDeactivationConfirmation();
      this.scimManagementService.closeGroupDetails();
    } catch (error) {
      console.error('Failed to deactivate group:', error);
    }
  }

  private async loadData() {
    await this.scimManagementService.loadData(this.availableRoles);
  }

  private async handleConfirmationApproved(onSuccess: () => void) {
    try {
      await this.scimManagementService.applyChanges();
      onSuccess();
    } catch (error) {
      console.error('Failed to apply changes:', error);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Workspace::Teammates::Scim': typeof Scim;
    'new-settings/workspace/teammates/scim': typeof Scim;
  }
}
